<script setup>
import { ref, computed } from 'vue'
import TableList from '../others/TableList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import CustomerDetails from '../customers/CustomerDetails.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Commons from '@/shared/utils/commons'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import { openChatWithClient } from '@/features/chat/chatUtils.js'
import { getClientUnreadCount } from '@/features/chat/chatState.js'

const deleteTrigger = ref(null)
const updateStatusTrigger = ref(null)
const updateProfileTrigger = ref(null)
const detailData = ref(null)

const cols = computed(() => [
  {
    name: 'Client',
    id: 'name',
    value(el) {
      return [el.name || 'Utilisateur', el.email || el.phone || '-']
    },
  },
  {
    name: 'Solde',
    id: 'balance',
    value(el) {
      if (el.balance === null || el.balance === undefined) return '0 F'
      return Commons.formatNumber(el.balance, { suffix: ' F' })
    },
  },
  {
    name: 'Stats',
    id: 'stats',
    value(el) {
      const stats = el.orderStats || {}
      return `
        <div class="text-[10px] leading-tight">
          <p><span class="text-gray-400 font-medium">CMDES:</span> <span class="font-bold text-gray-700">${stats.totalOrders || 0}</span></p>
          <p><span class="text-gray-400 font-medium">TOTAL:</span> <span class="font-bold text-primary-600">${Commons.formatNumber(stats.totalSpent || 0)} F</span></p>
        </div>
      `
    }
  },
  {
    name: 'Statut',
    id: 'status',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'Actif', value: 'active' },
        { name: 'Suspendu', value: 'suspended' },
      ]
    },
    value(el) {
      const status = el.status || 'active'
      const colors = {
        active: 'text-success-600 bg-success-50',
        actif: 'text-success-600 bg-success-50',
        suspended: 'text-warning-600 bg-warning-50',
        suspendu: 'text-warning-600 bg-warning-50',
        inactif: 'text-gray-600 bg-gray-50'
      }
      const colorClass = colors[status.toLowerCase()] || colors.inactif
      return `<span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}">${status}</span>`
    }
  },
  // {
  //   name: 'Inscription',
  //   id: 'createdAt',
  //   value(el) {
  //     return `<span class="text-xs text-gray-500">${Commons.time(el.createdAt)}</span>`
  //   },
  // },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/chat.svg',
    badge: (client) => getClientUnreadCount(client.id || client._id),
    onClick(client) {
      openChatWithClient(client)
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options(el) {
      const status = el.status || 'active'
      return [
        { name: 'Voir détails', value: 'view', icon: 'pi pi-eye' },
        // { name: status === 'active' ? 'Suspendre' : 'Activer', value: 'toggle_status', icon: 'pi pi-lock' },
        // { name: 'Supprimer', value: 'delete', icon: 'pi pi-trash' },
      ]
    },
    onClick(opt, el) {
      const action = typeof opt === 'object' ? opt.value : opt
      if (action === 'view') {
        detailData.value = el
      } else if (action === 'toggle_status') {
        const newStatus = (el.status || 'active') === 'active' ? 'suspended' : 'active'
        updateStatusTrigger.value.trigger({ id: el.id, status: newStatus })
      } else if (action === 'delete') {
        if (confirm(`Voulez-vous vraiment supprimer le client ${el.name || ''} ?`)) {
          deleteTrigger.value.trigger({ id: el.id })
        }
      }
    },
    value() {},
  },
])

const onSuccess = (msg) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: msg,
    type: 'success',
  })
  detailData.value = null
}

const onError = (msg) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: msg,
    type: 'error',
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <div class="mb-6">
      <h1 class="text-3xl font-black text-gray-900 mb-1">Gestion Clients</h1>
      <p class="text-gray-500 text-sm">Analysez les comportements d'achat et gérez les comptes utilisateurs</p>
    </div>

    <TableList
      title="Base Clients"
      dataKey="customers"
      hasSearch
      :cols="cols"
      :refreshOnEvents="['CUSTOMER_DELETED', 'CUSTOMER_UPDATED']"
    ></TableList>

    <ActionTrigger
      dataKey="deleteCustomer"
      ref="deleteTrigger"
      @success="onSuccess('Client supprimé avec succès')"
      @failed="err => onError(err?.message || 'Erreur lors de la suppression')"
      dispatchOnSuccess="CUSTOMER_DELETED"
    />

    <ActionTrigger
      dataKey="updateCustomerStatus"
      ref="updateStatusTrigger"
      @success="onSuccess('Statut du client mis à jour')"
      @failed="err => onError(err?.message || 'Erreur lors de la mise à jour')"
      dispatchOnSuccess="CUSTOMER_UPDATED"
    />

    <ActionTrigger
      dataKey="updateCustomerProfile"
      ref="updateProfileTrigger"
      @success="onSuccess('Profil du client mis à jour')"
      @failed="err => onError(err?.message || 'Erreur lors de la mise à jour')"
      dispatchOnSuccess="CUSTOMER_UPDATED"
    />

    <SlideDetailView :isVisible="!!detailData" @hide="detailData = null">
      <CustomerDetails 
        v-if="detailData" 
        :customerData="detailData" 
        @update-status="s => updateStatusTrigger.trigger({ id: detailData.id, status: s })"
        @update-profile="updates => updateProfileTrigger.trigger({ userId: detailData.id, updates })"
        @delete="() => { if(confirm('Supprimer ?')) deleteTrigger.trigger({ id: detailData.id }) }"
      />

    </SlideDetailView>
  </div>
</template>
